import React, { ReactNode, useCallback, useState } from "react";
import { Link } from "react-router-dom";

import Button from "veneer/Button";
import FilePicker from "veneer/FilePicker";
import Stack from "veneer/Stack";
import Typography from "veneer/Typography";

import { useAppDispatch } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas";
import { UPLOAD_APPLICATION } from "cortex/ui/src/redux-store/applications";
import BackButton from "cortex/ui/src/ui/button";
import Notification from "cortex/ui/src/ui/notification";
import {
  type ResponseLike,
  getErrorDetails,
} from "cortex/ui/src/util/response";
import { Container } from "cortex/ui/src/view/layout";

export default function UploadApplication() {
  const dispatch = useAppDispatch();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [errorDetails, setErrorDetails] = useState<ReactNode>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>("");

  const doUpload = useCallback(async () => {
    if (!selectedFile) {
      setErrorDetails("");
      setErrorMessage("Please select a file to upload.");
      setSuccessMessage("");
      setSubmitted(false);
      return;
    }

    try {
      await createPromiseApiAction(dispatch, UPLOAD_APPLICATION, [
        selectedFile,
      ]);

      setErrorDetails("");
      setErrorMessage("");
      setSuccessMessage("Application uploaded successfully.");
      setSubmitted(false);
      setSelectedFile(null);
    } catch (error: unknown) {
      const details = getErrorDetails(
        error as ResponseLike | null,
        "field_errors",
      );
      const message = "There are errors with your application upload.";

      setErrorDetails(details);
      setErrorMessage(message);
      setSuccessMessage("");
      setSubmitted(false);
    }
  }, [dispatch, selectedFile]);

  const upload = useCallback(() => {
    setSubmitted(true);
    void doUpload();
  }, [doUpload]);

  const onFileChange = useCallback((file: File | null) => {
    setSelectedFile(file);
    setErrorDetails("");
    setErrorMessage("");
    setSuccessMessage("");
    setSubmitted(false);
  }, []);

  return (
    <Container p={1} spacing={1} title="Upload Application">
      <Notification
        error={errorMessage}
        errorChildren={errorDetails}
        success={successMessage}
        successChildren={<Link to="/applications">Your Applications</Link>}
      />
      <Typography fontWeight="bold">Select application file</Typography>
      <Stack alignItems="center" direction="row" mt={1} spacing={1}>
        <FilePicker
          accept={[".zip", "application/zip"]}
          onChange={onFileChange}
          variant="contained"
        >
          Choose file
        </FilePicker>

        <Typography variant="body2">
          {selectedFile ? selectedFile.name : "No file selected"}
        </Typography>
      </Stack>
      <Typography variant="caption">
        Select the application zip file you want to upload. Your application
        will be uploaded as a testing version. You will need to manually mark it
        as stable once uploaded.
      </Typography>
      <Stack alignItems="center" direction="row" spacing={1}>
        <BackButton />
        <Button disabled={submitted} onClick={upload}>
          Save
        </Button>
      </Stack>
    </Container>
  );
}
